import { Ability, NavigationMenuEntry, Route, Widget } from "@/shared/environment/ability.types";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases.ts";
import { keyText } from "@/shared/i18n/translation-types.ts";
import AiAssistantContextWidget from "../widgets/ai-assistent-context.widget.vue";
import { useAiAssistantAccessAsync } from "@/shared/ai-assistant/ai-assistant-access-async.ts";

export const AI_ASSISTANT_ALIAS = "ai-assistant";

/**
 * Ability module for the AI Assistant feature.
 *
 * Access is controlled entirely through {@link init}: if the user does not have the
 * required permissions, `init()` returns `false` and `initializeAbilities()` excludes
 * the whole ability — routes, widgets, and navigation entries are never registered.
 *
 * @see {@link useAiAssistantAccessAsync} for the async access evaluation logic.
 */
export class AiAssistantAbility implements Ability {
    alias = AI_ASSISTANT_ALIAS;

    /**
     * Runs once during ability init, before routes are registered.
     *
     * Delegates entirely to {@link useAiAssistantAccessAsync}. The return value
     * is forwarded directly to `initializeAbilities()`:
     * - `true`  → ability is kept; routes, widgets, and navigation are registered normally.
     * - `false` → ability is excluded; nothing is registered for this user.
     *
     * @returns The result of the access check — `false` excludes the entire ability.
     */
    init = async (): Promise<boolean> => {
        return await useAiAssistantAccessAsync();
    };

    getNavigationMenuEntries = (): Array<NavigationMenuEntry> => {
        return [
            {
                title: keyText("ai-assistant.AI Assistant"),
                icon: "fak fa-regular fa-qit-ai-assistant-regular",
                /** Reflects the access state resolved in {@link init}. */
                internalRoute: {
                    alias: CommonRouteAliases.aiAssistant,
                    withContextParams: true,
                },
            },
        ];
    };

    getRoutes = (): Array<Route> => {
        return [
            {
                alias: CommonRouteAliases.aiAssistant,
                params: ["product?", "asset?"],
                getComponent: () => import("../pages/ai-assistant.vue"),
                isRoot: true,
            },
        ];
    };

    getWidgets = (): Array<Widget> => {
        return [
            {
                widgetContextAlias: ["product", "asset"],
                widgetComponent: AiAssistantContextWidget,
            },
        ];
    };
}
